"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { money } from "@dealerzone/libs/utils";
import { DZTypes } from "@dealerzone/libs";

interface HeroCarouselProps {
  vehicles: DZTypes.Vehicle[];
}

export default function HeroCarousel({ vehicles }: HeroCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (vehicles.length <= 1) {
      return;
    }

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % vehicles.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [vehicles.length]);

  if (vehicles.length === 0) {
    return null;
  }

  const vehicle = vehicles[currentIndex];
  const imageUrl = vehicle.main_image?.large || vehicle.images?.[0]?.large || "/images/placeholder.jpg";

  function goToSlide(index: number) {
    setCurrentIndex(index);
  }

  function goToPrev() {
    setCurrentIndex((prev) => (prev - 1 + vehicles.length) % vehicles.length);
  }

  function goToNext() {
    setCurrentIndex((prev) => (prev + 1) % vehicles.length);
  }

  return (
    <section className="relative bg-gradient-to-br from-zinc-800 to-zinc-900 overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(220,38,38,0.3),transparent_50%)]"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-12 md:py-20">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="text-white order-2 md:order-1">
            <p className="text-red-400 font-medium mb-2">{vehicle.make}</p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </h1>
            <p className="text-2xl md:text-3xl font-semibold text-zinc-300 mb-6">
              {money(vehicle.price)}
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              {vehicle.title_status && (
                <span className={`px-3 py-1 rounded-full text-sm capitalize ${vehicle.is_clean ? "bg-blue-600" : "bg-zinc-700"}`}>
                  {vehicle.title_status}
                </span>
              )}
              {vehicle.body && (
                <span className="px-3 py-1 bg-zinc-700 rounded-full text-sm capitalize">
                  {vehicle.body}
                </span>
              )}
            </div>
            <Link
              href={`/vehicle/${vehicle.slug}`}
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              View Vehicle
              <i className="bootstrap-arrow-right"></i>
            </Link>
          </div>

          <div className="order-1 md:order-2">
            <Link href={`/vehicle/${vehicle.slug}`} className="block relative aspect-image rounded-xl overflow-hidden shadow-2xl">
              <Image
                src={imageUrl}
                alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                fill
                className="object-cover transition-transform duration-500 hover:scale-105"
                priority
              />
            </Link>
          </div>
        </div>

        {vehicles.length > 1 && (
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={goToPrev}
              className="p-2 text-white/70 hover:text-white transition-colors"
              aria-label="Previous vehicle"
            >
              <i className="bootstrap-chevron-left text-xl"></i>
            </button>

            <div className="flex gap-2">
              {vehicles.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    index === currentIndex ? "bg-red-500" : "bg-white/30 hover:bg-white/50"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={goToNext}
              className="p-2 text-white/70 hover:text-white transition-colors"
              aria-label="Next vehicle"
            >
              <i className="bootstrap-chevron-right text-xl"></i>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
